import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Film, PlayCircle, Loader2, Link2, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCrypto } from "../context/CryptoContext";
import { encryptAndUpload, encryptAndUploadWithKey, validateVideoFile } from "../lib/mediaUpload";
import * as C from "../lib/crypto";
import { useNetText } from "./HearthEvents";
import { RecordingTip } from "./RecordingPlaybook";

const parse = (s) => { try { return JSON.parse(s); } catch { return null; } };
const REPLAYS = "Live session replays";

// Header (part="head") or button (part="button") for lesson shares and session recaps; link data is network-key encrypted.
export const PostCta = ({ post, part }) => {
  const raw = useNetText(post.cta_ct, post.cta_iv);
  const cta = raw ? parse(raw) : null;
  const recap = post.kind === "recap";
  const Icon = recap ? Film : BookOpen;
  const btn = "inline-flex items-center gap-1.5 bg-[#14A89A] text-[#0A0A0A] font-semibold font-body text-xs px-3 py-1.5 hover:brightness-110";
  if (part === "button") {
    if (!cta?.url) return null;
    return (
      <div className="mt-3 mb-1">
        {cta.url.startsWith("/")
          ? <Link to={cta.url} data-testid={`hearth-cta-btn-${post.id}`} className={btn}><BookOpen size={13} /> {cta.label || "Open lesson"}</Link>
          : <a href={cta.url} target="_blank" rel="noopener noreferrer" data-testid={`hearth-cta-btn-${post.id}`} className={btn}><PlayCircle size={13} /> {cta.label || "Watch replay"}</a>}
      </div>
    );
  }
  return (
    <div className="mb-2" data-testid={`hearth-cta-${post.id}`}>
      <div className="font-body text-[10px] uppercase tracking-wider text-[#14A89A] flex items-center gap-1 mb-1"><Icon size={11} /> {recap ? "Session recap" : "New lesson"}</div>
      {cta?.headline && <div className="font-body text-white font-medium" data-testid={`hearth-cta-headline-${post.id}`}>{cta.headline}</div>}
    </div>
  );
};

const LessonOption = ({ on, setOn, beacon, sid, setSid, lessonTitle, setLessonTitle, hasReplay }) => (
  <div className="border-t border-white/10 pt-2.5">
    <label className={`flex items-center gap-2 font-body text-xs cursor-pointer ${hasReplay ? "text-zinc-300" : "text-zinc-600"}`}>
      <input type="checkbox" checked={on} disabled={!hasReplay} onChange={(e) => setOn(e.target.checked)} data-testid="recap-to-lesson" className="accent-[#14A89A]" />
      <BookOpen size={12} className="text-zinc-500" /> Also add the replay as a Beacon lesson (subscribers only)
    </label>
    {!hasReplay && <p className="font-body text-[11px] text-zinc-600 mt-1 ml-5">Add a replay video or link first.</p>}
    {on && beacon && (
      <div className="mt-2 ml-5 space-y-2">
        <input value={lessonTitle} onChange={(e) => setLessonTitle(e.target.value)} maxLength={160} placeholder="Lesson title" data-testid="recap-lesson-title"
          className="w-full bg-[#121212] border border-white/10 focus:border-[#14A89A] px-3 py-2 text-white font-body text-sm outline-none" />
        <select value={sid} onChange={(e) => setSid(e.target.value)} data-testid="recap-lesson-section"
          className="w-full bg-[#121212] border border-white/10 px-3 py-2 text-white font-body text-sm outline-none">
          {(beacon.sections || []).map((s) => <option key={s.id} value={s.id}>{s.title || "Untitled section"}</option>)}
          {!(beacon.sections || []).some((s) => s.title === REPLAYS) && <option value="new">+ New section: "{REPLAYS}"</option>}
        </select>
        <p className="font-body text-[11px] text-zinc-500">Your notes become the lesson description. The replay is encrypted again with your Beacon key{" "}so only subscribers can watch.</p>
      </div>
    )}
  </div>
);

// Owner: turn an ended live session into a recap post (notes + uploaded video or replay link).
export const RecapForm = ({ hearthId, eventPost, title, onDone }) => {
  const { encryptNetText, encryptNetBytes, hasNetwork, ensureMyBeaconKey } = useCrypto();
  const [notes, setNotes] = useState("");
  const [toLesson, setToLesson] = useState(false);
  const [beacon, setBeacon] = useState(null);
  const [sid, setSid] = useState("new");
  const [lessonTitle, setLessonTitle] = useState(title || "");
  const [mode, setMode] = useState("upload");
  const [file, setFile] = useState(null);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState("");
  const hasReplay = (mode === "upload" && !!file) || (mode === "link" && !!link.trim());
  const pick = async (f) => { if (!f) return; const err = await validateVideoFile(f); if (err) return toast.error(err); setFile(f); };
  const save = async () => {
    if (!hasNetwork()) return toast.error("Unlock your vault first");
    if (mode === "link" && link.trim() && !/^https:\/\/\S+$/i.test(link.trim())) return toast.error("Replay link must start with https://");
    if (!notes.trim() && !file && !link.trim()) return toast.error("Add notes or a replay");
    try {
      const body = { kind: "recap", ref: eventPost.id, enc: true, media: [] };
      const ctaData = { headline: `Recap: ${title || "Live session"}` };
      if (mode === "link" && link.trim()) Object.assign(ctaData, { url: link.trim(), label: "Watch replay" });
      const cta = await encryptNetText(JSON.stringify(ctaData));
      Object.assign(body, { cta_ct: cta.ct, cta_iv: cta.iv });
      if (notes.trim()) { const e = await encryptNetText(notes.trim()); body.ct = e.ct; body.iv = e.iv; }
      if (mode === "upload" && file) { setBusy("Encrypting & uploading replay…"); body.media = [await encryptAndUpload(file, { encryptNetBytes, hasNetwork, onPct: () => {} })]; }
      setBusy("Posting…");
      await api.post(`/hearths/${hearthId}/posts`, body);
      let added = false;
      if (toLesson && hasReplay) added = await saveLesson();
      toast.success(added ? "Recap posted and replay added to your Beacon" : "Recap posted to your Hearth");
      onDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't post the recap"); } finally { setBusy(""); }
  };
  // Replay → subscriber-only Beacon lesson (re-encrypted with the Beacon key).
  const openLesson = async (on) => {
    setToLesson(on);
    if (!on || beacon) return;
    try { const { data } = await api.get("/beacon/mine"); const secs = data.beacon?.sections || []; setBeacon(data.beacon); setSid((secs.find((s) => s.title === REPLAYS) || secs[0])?.id || "new"); }
    catch { toast.error("Couldn't load your Beacon"); setToLesson(false); }
  };
  const saveLesson = async () => {
    let newSection = null;
    try {
      setBusy("Adding replay to your Beacon…");
      const key = await ensureMyBeaconKey(beacon.id);
      if (!key) throw new Error("key");
      let sectionId = sid;
      if (sid === "new") sectionId = newSection = (await api.post("/beacon/mine/sections", { title: REPLAYS })).data.id;
      const payload = { section_id: sectionId, title: lessonTitle.trim() || title || "Live session replay", enc: true };
      if (notes.trim()) { const e = await C.encryptText(key, notes.trim()); payload.caption = e.ctB64; payload.caption_iv = e.ivB64; }
      if (mode === "upload" && file) {
        const m = await encryptAndUploadWithKey(file, (b) => C.encryptBytes(key, b), "video");
        Object.assign(payload, { type: "video", media: m.path, media_iv: m.iv || null });
      } else Object.assign(payload, { type: "link", link_url: link.trim(), link_label: "Watch replay" });
      await api.post("/beacon/mine/items", payload);
      return true;
    } catch {
      if (newSection) api.delete(`/beacon/mine/sections/${newSection}`).catch(() => {});
      toast.error("Recap posted, but the Beacon lesson couldn't be added. You can add it from your builder.");
      return false;
    }
  };
  const tab = (m, Icon, l) => (
    <button onClick={() => setMode(m)} data-testid={`recap-mode-${m}`} className={`flex items-center gap-1.5 px-3 py-1.5 font-body text-xs border ${mode === m ? "border-[#14A89A] text-[#14A89A] bg-[#14A89A]/10" : "border-white/10 text-zinc-400"}`}><Icon size={13} /> {l}</button>
  );
  return (
    <div className="border border-[#14A89A]/40 bg-[#0A0A0A] p-3 mt-3 space-y-2.5" data-testid={`recap-form-${eventPost.id}`}>
      <div className="font-body text-xs uppercase tracking-wider text-[#14A89A] flex items-center gap-1.5"><Film size={13} /> Post a recap</div>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={4000} placeholder="Key takeaways, resources mentioned, what's next…" data-testid="recap-notes"
        className="w-full bg-[#121212] border border-white/10 focus:border-[#14A89A] px-3 py-2 text-white font-body text-sm outline-none resize-none" />
      <div className="flex gap-2">{tab("upload", Upload, "Upload video")}{tab("link", Link2, "Paste link")}</div>
      <RecordingTip />
      {mode === "upload"
        ? <label className="flex items-center gap-2 border border-dashed border-white/15 px-3 py-2.5 font-body text-xs text-zinc-400 cursor-pointer hover:border-[#14A89A]/50" data-testid="recap-video">
            <Upload size={14} /> {file ? file.name : "Choose the replay video (up to 15 min / 500 MB, encrypted)"}
            <input type="file" accept="video/*" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
          </label>
        : <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://… (YouTube, Proton Drive, etc.)" data-testid="recap-link"
            className="w-full bg-[#121212] border border-white/10 focus:border-[#14A89A] px-3 py-2 text-white font-body text-sm outline-none" />}
      <LessonOption on={toLesson} setOn={openLesson} beacon={beacon} sid={sid} setSid={setSid} lessonTitle={lessonTitle} setLessonTitle={setLessonTitle} hasReplay={hasReplay} />
      <div className="flex items-center justify-end gap-2">
        {busy && <span className="font-body text-[11px] text-zinc-500 mr-auto">{busy}</span>}
        <button onClick={() => onDone(false)} className="font-body text-xs text-zinc-400 px-3 py-1.5">Cancel</button>
        <button onClick={save} disabled={!!busy} data-testid="recap-post" className="bg-[#14A89A] text-[#0A0A0A] font-semibold font-body text-xs px-4 py-1.5 flex items-center gap-1.5 disabled:opacity-40">
          {busy && <Loader2 size={12} className="animate-spin" />} Post recap
        </button>
      </div>
    </div>
  );
};
